/**
 * Напоминание по задаче: дата и время, быстрые варианты. Приходит
 * исполнителям — в колокольчик на сайте, push'ем в приложении и письмом.
 *
 * Время — по часам человека: «17 октября в 15:30» означает 15:30 там,
 * где он сейчас, и сервер получает момент с поясом, а не голые цифры.
 */

import { useState } from "react";

import type { WsError } from "@taskflow/client-core";
import { joinLocal, reminderLabel, splitLocal } from "@taskflow/client-core/notes";
import { type TaskDetail, useUpdateTaskMutation } from "@taskflow/client-core/tasks";
import { Button, DateInput, FormField, Input, Modal, toast } from "@/shared/ui";

type Preset = "hour" | "evening" | "morning";

function preset(kind: Preset): string {
  const now = new Date();
  const moment = new Date(now);
  if (kind === "hour") {
    moment.setMinutes(0, 0, 0);
    moment.setHours(now.getHours() + 1);
    if (moment.getTime() - now.getTime() < 30 * 60 * 1000) moment.setHours(moment.getHours() + 1);
  } else if (kind === "evening") {
    moment.setHours(18, 0, 0, 0);
    if (moment <= now) moment.setDate(moment.getDate() + 1);
  } else {
    moment.setDate(now.getDate() + 1);
    moment.setHours(9, 0, 0, 0);
  }
  return moment.toISOString();
}

/** Сработавшее напоминание в прошлом переносят на будущее — исходным
 *  значением берётся ближайший вечер. */
function initialMoment(existing: string | null): string {
  return existing !== null && new Date(existing).getTime() > Date.now() ? existing : preset("evening");
}

export function TaskReminderDialog({ task, onClose }: { task: TaskDetail; onClose: () => void }) {
  const existing = task.remindAt ?? null;
  const initial = splitLocal(initialMoment(existing));
  const [date, setDate] = useState(initial.date);
  const [time, setTime] = useState(initial.time);
  const [error, setError] = useState<string | null>(null);
  const [updateTask, { isLoading: saving }] = useUpdateTaskMutation();

  const save = async (remindAt: string | null) => {
    try {
      await updateTask({ key: task.key, version: task.version, remindAt }).unwrap();
      if (remindAt === null) toast.success("Напоминание снято");
      else toast.success("Напоминание установлено", reminderLabel(remindAt));
      onClose();
    } catch (raw) {
      const failure = raw as WsError;
      setError(
        failure.status === 409
          ? "Задачу изменили параллельно — закройте окно и попробуйте ещё раз"
          : (failure.data?.message ?? "Не удалось сохранить напоминание"),
      );
    }
  };

  const submit = () => {
    const moment = date && time ? joinLocal(date, time) : null;
    if (moment === null) {
      setError("Укажите дату и время");
      return;
    }
    if (new Date(moment).getTime() <= Date.now()) {
      setError("Это время уже прошло — выберите время в будущем");
      return;
    }
    void save(moment);
  };

  return (
    <Modal
      open
      onClose={onClose}
      width="sm"
      title="Напоминание по задаче"
      footer={
        <div className="flex w-full flex-wrap items-center gap-2">
          {existing !== null && (
            <Button variant="ghost" size="sm" disabled={saving} onClick={() => void save(null)}>
              Убрать напоминание
            </Button>
          )}
          <span className="flex-1" />
          <Button variant="secondary" size="sm" onClick={onClose}>
            Отмена
          </Button>
          <Button size="sm" loading={saving} onClick={submit}>
            Сохранить
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-[13px] text-muted">
          {task.assignees.length > 0
            ? "В выбранное время исполнители получат уведомление."
            : "Исполнителей пока нет — напоминание получит постановщик задачи."}
        </p>

        <div className="flex flex-wrap gap-2" role="group" aria-label="Быстрый выбор">
          {(
            [
              ["hour", "Через час"],
              ["evening", "В 18:00"],
              ["morning", "Завтра в 9:00"],
            ] as const
          ).map(([kind, label]) => (
            <Button
              key={kind}
              variant="secondary"
              size="sm"
              onClick={() => {
                const next = splitLocal(preset(kind));
                setDate(next.date);
                setTime(next.time);
                setError(null);
              }}
            >
              {label}
            </Button>
          ))}
        </div>

        <div className="grid grid-cols-[1fr_7.5rem] gap-3">
          <FormField label="Дата" error={error}>
            {(field) => (
              <DateInput
                {...field}
                value={date}
                onChange={(next) => {
                  setDate(next);
                  setError(null);
                }}
              />
            )}
          </FormField>
          <FormField label="Время">
            {(field) => (
              <Input
                {...field}
                type="time"
                value={time}
                onChange={(event) => {
                  setTime(event.target.value);
                  setError(null);
                }}
              />
            )}
          </FormField>
        </div>
      </div>
    </Modal>
  );
}
